import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../utils/auth';
import { useWebSocket } from '../utils/WebSocketContext';
import Navigation from '../components/Navigation';
import { tasksApi } from '../utils/api';
import type { TaskListItem } from '../types/task';
import { TaskStatus } from '../types/task';

const getStatusColor = (status: TaskStatus): string => {
  switch (status) {
    case TaskStatus.PENDING:
      return 'bg-gray-100 text-gray-700';
    case TaskStatus.PROCESSING:
      return 'bg-blue-100 text-blue-700';
    case TaskStatus.COMPLETED:
      return 'bg-green-100 text-green-700';
    case TaskStatus.FAILED:
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const getStatusLabel = (status: TaskStatus): string => {
  return status.charAt(0).toUpperCase() + status.slice(1);
};

export default function DashboardPage() {
  const { user, isAdmin } = useAuth();
  const { lastUpdate } = useWebSocket();
  const [recentTasks, setRecentTasks] = useState<TaskListItem[]>([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState(true);

  const loadRecentTasks = async () => {
    try {
      const tasks = await tasksApi.getAll();
      setRecentTasks(tasks.slice(0, 5)); // Get 5 most recent tasks
    } catch (err) {
      console.error('Failed to load recent tasks:', err);
    } finally {
      setIsLoadingTasks(false);
    }
  };

  useEffect(() => {
    loadRecentTasks();
  }, []);

  // Listen for WebSocket task updates
  useEffect(() => {
    if (lastUpdate) {
      setRecentTasks(prevTasks => {
        const index = prevTasks.findIndex(t => t.id === lastUpdate.id);
        if (index !== -1) {
          // Update existing task
          const updatedTasks = [...prevTasks];
          updatedTasks[index] = {
            ...updatedTasks[index],
            status: lastUpdate.status as TaskStatus,
            updated_at: lastUpdate.updated_at
          };
          return updatedTasks;
        } else {
          // Task not in list, reload to potentially include it
          loadRecentTasks();
          return prevTasks;
        }
      });
    }
  }, [lastUpdate]);

  return (
    <>
      <Helmet>
        <title>Dashboard - MAIA Agent 128</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12">
        <div className="mb-8 fade-in">
          <h1 className="text-3xl font-light mb-2">
            Welcome, <span className="font-medium">{user?.username}</span>
          </h1>
          <p className="text-gray-600">Agent dashboard</p>
        </div>

        {/* Universal Agent 128 Card */}
        <div className="mb-8 fade-in stagger-1">
          <div className="bg-gradient-to-r from-gray-900 to-gray-700 text-white rounded-lg p-8 shadow-hover">
            <h2 className="text-2xl font-light mb-3">Universal Agent 128</h2>
            <p className="text-gray-300 mb-6">
              Submit tasks for AI processing with iterative refinement
            </p>
            <div className="flex space-x-4">
              <Link
                to="/tasks/new"
                className="inline-block px-6 py-3 bg-white text-black rounded-md hover:bg-gray-100 transition-colors text-sm font-medium"
              >
                Create Task
              </Link>
              <Link
                to="/tasks"
                className="inline-block px-6 py-3 border border-white text-white rounded-md hover:bg-white/10 transition-colors text-sm font-medium"
              >
                View Tasks
              </Link>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* User Profile Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 card-hover shadow-subtle fade-in stagger-2">
            <h2 className="text-lg font-medium mb-4">Your Profile</h2>
            <div className="space-y-3">
              <div>
                <span className="text-sm text-gray-500">Username</span>
                <p className="font-medium">{user?.username}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Email</span>
                <p className="font-medium">{user?.email}</p>
              </div>
              {user?.full_name && (
                <div>
                  <span className="text-sm text-gray-500">Full Name</span>
                  <p className="font-medium">{user.full_name}</p>
                </div>
              )}
              <div>
                <span className="text-sm text-gray-500">Role</span>
                <p className="font-medium">{isAdmin ? 'Administrator' : 'User'}</p>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 card-hover shadow-subtle fade-in stagger-3">
            <h2 className="text-lg font-medium mb-4">Account Status</h2>
            <div className="space-y-3">
              <div>
                <span className="text-sm text-gray-500">Status</span>
                <p className="font-medium text-green-600">Active</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Member Since</span>
                <p className="font-medium">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Last Updated</span>
                <p className="font-medium">
                  {user?.updated_at ? new Date(user.updated_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* System Info */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 card-hover shadow-subtle fade-in stagger-4">
            <h2 className="text-lg font-medium mb-4">System Info</h2>
            <div className="space-y-3">
              <div>
                <span className="text-sm text-gray-500">Platform</span>
                <p className="font-medium">Agent128</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Version</span>
                <p className="font-medium">2.0.0</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">API</span>
                <p className="font-medium text-green-600">Connected</p>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Tasks */}
        <div className="mt-8 fade-in stagger-5">
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-subtle">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-medium">Recent Tasks</h2>
              <Link to="/tasks" className="text-sm text-gray-600 hover:text-black">
                View All →
              </Link>
            </div>

            {isLoadingTasks ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black mx-auto"></div>
              </div>
            ) : recentTasks.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-4">No tasks yet</p>
                <Link
                  to="/tasks/new"
                  className="inline-block px-4 py-2 btn-primary rounded-md text-sm font-medium"
                >
                  Create Your First Task
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {recentTasks.map((task) => (
                  <Link
                    key={task.id}
                    to={`/tasks/${task.id}`}
                    className="block p-4 border border-gray-200 rounded-md hover:border-gray-400 transition-colors"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2 flex-1">
                        {task.unread && (
                          <span className="w-2.5 h-2.5 bg-blue-500 rounded-full inline-block flex-shrink-0"></span>
                        )}
                        <h3 className={`font-medium text-gray-900 ${task.unread ? 'font-semibold' : ''}`}>{task.title}</h3>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(task.status)}`}>
                        {getStatusLabel(task.status)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-1 mb-2">
                      {task.description}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>Last updated: {new Date(task.updated_at).toLocaleDateString()}</span>
                      <span>ID: {task.id}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Admin Tools */}
        {isAdmin && (
          <div className="mt-8 fade-in stagger-5">
            <div className="bg-black text-white rounded-lg p-6 shadow-hover">
              <h2 className="text-xl font-light mb-4">Admin Tools</h2>
              <p className="text-gray-300 mb-6">
                Manage users and system settings
              </p>
              <Link
                to="/users"
                className="inline-block px-6 py-3 bg-white text-black rounded-md hover:bg-gray-100 transition-colors text-sm font-medium"
              >
                Manage Users
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
}
